/**
 * Converts time to readable string
 * @param {Number} minutes
 * @returns String
 */
export function formatTime(minutes: number): string {
   const hours = minutes / 60;
   const rHours = Math.floor(hours);

   const min = (hours - rHours) * 60;
   const rMin = Math.round(min);

   let time = "";

   if (rHours) {
      time += `${rHours} ${rHours > 1 ? "hours" : "hour"}`;
   }

   if (rMin) {
      time += ` ${rMin} ${rMin > 1 ? "minutes" : "minute"}`;
   }

   return time;
}
const LANGUAGE_TEXT = { en: "English", es: "Spanish", zh: "Chinese" } as const;

export function languageText(lang: keyof typeof LANGUAGE_TEXT): string {
   return LANGUAGE_TEXT[lang];
}
export function slugify(text: string): string {
   return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w\-]+/g, "") // Remove all non-word chars
      .replace(/\-\-+/g, "-") // Replace multiple - with single -
      .replace(/^-+/, "") // Trim - from start of text
      .replace(/-+$/, ""); // Trim - from end of text
}

export function isHtml(str: string): boolean {
   return /<\/?[a-z][\s\S]*>/i.test(str);
}

export function htmlToPlainText(html: string): string {
   const withLineBreaks = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6])>/gi, "\n");
   const doc = new DOMParser().parseFromString(withLineBreaks, "text/html");
   return (doc.body.textContent ?? "").trim();
}

export function convertType(
   type: string,
   discipline: number | string,
   plural: boolean = false,
): string | void {
   if (typeof discipline === "number") {
      discipline = disciplineText(discipline);
   }

   switch (type) {
      case "lesson_plan":
         return plural ? "Lesson Plans" : "Lesson Plan";
      case "practitioner":
         return "Practitioners";
      case "skill":
         if (discipline === "music") {
            return "Practice, Performance, & Literacy";
         } else if (discipline === "drama") {
            return "Skills & Collaborative Practices";
         } else if (discipline === "dance") {
            return "Dancers' Toolbox";
         }
         break;
      case "tls":
         return "Teaching & Learning Supports";
   }
}

const SEARCH_LABELS = {
   teachables: "Resources",
   artistic_insights: "Artistic Insights",
   core_works: "Core Works",
   help_center_topics: "Help Center",
   unit_sequences: "Unit Sequences",
} as const;

const PAGE_TYPES = {
   teachables: "teachable",
   artistic_insights: "artistic-insight",
   core_works: "core-work",
   help_center_topics: "help-center-topic",
   unit_sequences: "unit",
} as const;

export function formatSearchLabel(type: string): string | undefined {
   return SEARCH_LABELS[type as keyof typeof SEARCH_LABELS];
}

export function formatPageType(type: string): string | undefined {
   return PAGE_TYPES[type as keyof typeof PAGE_TYPES];
}

export function getAges(age: string): string {
   return age === "Ensemble" ? age : age + " years";
}
export function ageMapper(
   ages: Array<{ age_ranges?: string[] }> | undefined,
): Array<string> {
   return ages?.map((group) => group.age_ranges ?? []).flat() ?? [];
}

/**
 *
 * @param id
 * @returns Returns the text value of the Discipline ID
 */
export function disciplineText(id: number): Disciplines {
   return id === 1 ? "music" : id === 2 ? "dance" : "drama";
}

/**
 *
 * @param name
 * @returns
 */
export function disciplineId(
   name: Disciplines | string | string[] | undefined,
): number {
   if (!name) return 0;
   const value = Array.isArray(name) ? name[0] : name;
   return value === "music" ? 1 : value === "dance" ? 2 : 3;
}

/**
 *
 * @param target, duration
 */
export function scrollToEl(
   target: string,
   duration: number = 0.6,
   offset: number = -32,
): Promise<boolean> {
   const { $lenis } = useNuxtApp();
   return new Promise((resolve) => {
      $lenis?.scrollTo(target, {
         duration,
         offset,
         lock: true,
         easing: (x: number) =>
            x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2,
         onComplete: () => {
            resolve(true);
         },
      });
   });
}

export function sortCardsAlpha<T extends { title: string }>(cards: T[]): T[] {
   return [...cards].sort((a, b) => {
      const nameA = a.title.toLowerCase();
      const nameB = b.title.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
   });
}

export function getFontColor(incBgColor?: string): string {
   let rgb;
   let bgColor = incBgColor || "#ffffff";

   if (bgColor.startsWith("#")) {
      // prep hex
      bgColor = bgColor.replace(
         /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
         (m, r, g, b) => "#" + r + r + g + g + b + b,
      );
      const match = bgColor.substring(1).match(/.{2}/g);
      match && (rgb = match.map((x) => parseInt(x, 16)));
   } else if (bgColor.startsWith("rgb")) {
      // prep rgb
      const match = bgColor.match(/\d+/g);
      match && (rgb = match.map(Number));
   }
   // // Calculate brightness using the luminance formula
   const brightness =
      rgb && Math.round((rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000);
   // Determine text color based on brightness
   return brightness !== undefined && brightness < 125 ? "white" : "black";
}

export function normalizeFetchError(err: unknown) {
   const e = err as {
      statusCode?: number;
      status?: number;
      response?: { status?: number; _data?: { statusCode?: number; message?: string } };
      data?: { status?: number; message?: string };
      message?: string;
   };
   const status =
      e?.statusCode ||
      e?.status ||
      e?.response?.status ||
      e?.response?._data?.statusCode ||
      (e?.data && e.data.status) ||
      500;
   const message =
      e?.data?.message ||
      e?.response?._data?.message ||
      e?.message ||
      "Unknown error";
   return { status: Number(status) || 500, message, raw: err };
}

// Pulls the `{ error, status }` shape out of a Nuxt $fetch error so callers
// can return field-level validation errors to forms (used by useProfile's
// updatePassword / updateProfile). Both `data` (FetchError) and
// `response._data` (FetchResponse) carry the same ApiErrorBody.
export function extractFormError(err: unknown): FormErrorReturn {
   const e = err as {
      data?: ApiErrorBody;
      response?: { _data?: ApiErrorBody; status?: number };
      statusCode?: number;
   };
   return {
      error: e?.data?.error ?? e?.response?._data?.error,
      status: e?.statusCode ?? e?.response?.status,
   };
}
